import { CommonModule } from '@angular/common';
import { Component, input, output, computed } from '@angular/core';

/**
 * Interfaz para las opciones del menú contextual
 */
export interface ContextMenuOption {
  /** Identificador único de la opción */
  id: string;
  /** Texto a mostrar */
  label: string;
  /** Icono: nombre de PrimeIcons (ej: 'pi-info-circle') o de Material Symbol (ej: 'search_activity') si se usa iconClass */
  icon: string;
  /** Si la opción está deshabilitada */
  disabled?: boolean;
  /** Clase CSS adicional para el botón */
  class?: string;
  /** Clase CSS para el icono (ej: 'material-symbols-outlined icon-small'). Si se define, el icono se renderiza como Material Symbol en lugar de PrimeIcons */
  iconClass?: string;
}

/**
 * Componente reutilizable de menú contextual con posicionamiento responsivo
 *
 * @example
 * ```html
 * <app-context-menu
 *   [visible]="menuVisible()"
 *   [x]="menuPosition().x"
 *   [y]="menuPosition().y"
 *   [options]="menuOptions"
 *   (optionSelected)="handleMenuOption($event)"
 *   (close)="closeMenu()"
 * />
 * ```
 */
@Component({
  selector: 'app-context-menu',
  imports: [CommonModule],
  template: `
    @if (visible()) {
      <!-- Backdrop transparente para cerrar al hacer click fuera -->
      <div class="fixed inset-0 z-40" (click)="handleClose()"></div>
      
      <!-- Menú contextual: max-h y overflow-y-auto para pantallas muy pequeñas -->
      <div
        class="fixed z-50 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg shadow-lg py-2 w-[180px] max-w-[calc(100vw-20px)] max-h-[calc(100vh-20px)] overflow-y-auto"
        [style.left.px]="adjustedPosition().x"
        [style.top.px]="adjustedPosition().y">
        
        @for (option of options(); track option.id) {
          @if (isSeparator(option)) {
            <hr class="my-1 border-t border-gray-200 dark:border-gray-600" aria-hidden="true" />
          } @else {
            <button
              (click)="handleOptionClick(option)"
              [disabled]="option.disabled"
              [class]="option.class"
              class="w-full px-4 py-2 text-left text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 flex items-center gap-2 transition-colors cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed">
              @if (option.iconClass) {
                <span [class]="option.iconClass" aria-hidden="true">{{ option.icon }}</span>
              } @else {
                <i [class]="'pi ' + option.icon + ' text-sm'" aria-hidden="true"></i>
              }
              {{ option.label }}
            </button>
          }
        }
      </div>
    }
  `,
  standalone: true
})
export class ContextMenuComponent {
  /** Visibilidad del menú */
  visible = input<boolean>(false);

  /** Posición X del menú */
  x = input<number>(0);

  /** Posición Y del menú */
  y = input<number>(0);

  /** Opciones del menú */
  options = input<ContextMenuOption[]>([]);

  /** Ancho del menú (para cálculos de posicionamiento) */
  menuWidth = input<number>(180);

  /** Alto estimado del menú (para cálculos de posicionamiento). Si no se proporciona, se calcula según las opciones. */
  menuHeight = input<number | undefined>(undefined);

  /** Evento emitido cuando se selecciona una opción */
  optionSelected = output<ContextMenuOption>();

  /** Evento emitido cuando se cierra el menú */
  close = output<void>();

  /**
   * Alto estimado del menú calculado a partir del número de opciones.
   * Cada opción normal ~38px, separador ~14px, padding del contenedor ~16px.
   */
  private estimatedMenuHeight = computed(() => {
    const opts = this.options();
    const basePadding = 16;
    const total = opts.reduce((acc, o) => {
      const isSeparator = !o.label || o.id === 'separator' || o.class?.includes('border-t');
      return acc + (isSeparator ? 14 : 38);
    }, basePadding);
    return Math.max(80, total);
  });

  /**
   * Posición ajustada del menú para evitar que se salga de la pantalla.
   * En pantallas pequeñas o para filas inferiores, el menú se desplaza hacia arriba si no hay espacio abajo.
   */
  adjustedPosition = computed(() => {
    const xPos = this.x();
    const yPos = this.y();
    const width = this.menuWidth();
    const height = this.menuHeight() ?? this.estimatedMenuHeight();

    const windowWidth = window.innerWidth;
    const windowHeight = window.innerHeight;
    const margin = 10;

    // Calcular posición X (evitar que se salga por la derecha o izquierda)
    let adjustedX = xPos;
    if (adjustedX + width > windowWidth - margin) {
      adjustedX = windowWidth - width - margin;
    }
    adjustedX = Math.max(margin, adjustedX);

    // Calcular posición Y: si no hay espacio abajo, mostrar el menú encima del punto de clic
    let adjustedY = yPos;
    if (adjustedY + height > windowHeight - margin) {
      adjustedY = windowHeight - height - margin;
    }
    if (adjustedY < margin) {
      adjustedY = margin;
    }

    return { x: adjustedX, y: adjustedY };
  });

  /** Detecta si una opción es un separador (no debe renderizarse como botón) */
  protected isSeparator(option: ContextMenuOption): boolean {
    return !option.label || option.id === 'separator' || option.id.startsWith('sep');
  }

  /**
   * Maneja el click en una opción del menú
   */
  handleOptionClick(option: ContextMenuOption): void {
    if (!option.disabled) {
      this.optionSelected.emit(option);
      this.handleClose();
    }
  }

  /**
   * Maneja el cierre del menú
   */
  handleClose(): void {
    this.close.emit();
  }
}
